import "@progress/kendo-ui/js/kendo.colorpicker";
import * as ko from "knockout";
import * as Constants from "../common/constants";
import * as Global from "../common/global";
import * as UI from "../common/ui";
import * as customerSettingsData from "../data/customerSettingsData";
import { IApplication } from "../interfaces/application";
import * as AvailableRoles from "../interfaces/availableRoles";
import { IController } from "../interfaces/controller";
import * as Data from "../interfaces/data";

interface IViewModel {
    customerID: KnockoutObservable<number>;
    ColorSettings: KnockoutObservableArray<ICustomerSettingObservable>;
    ReportEmailSettings: KnockoutObservableArray<ICustomerSettingObservable>;
    CustomerImages: KnockoutObservableArray<ICustomerImageObservable>;
    ColorSettingTypes: KnockoutObservableArray<customerSettingsData.ISettingType>;
    ReportEmailSettingTypes: KnockoutObservableArray<customerSettingsData.ISettingType>;

    RegistrationCodes: KnockoutObservableArray<ICustomerSettingObservable>;
    registrationCodeValue: KnockoutObservable<string>;
    registrationCodeSettingTypeID: number;
    selfRegistrationAllowed: boolean;
    addRegistrationCode: () => void;
    removeRegistrationCode: (index: KnockoutObservable<number>) => void;

    AvailableUserRoles: KnockoutObservableArray<IRoleSelectionObservable>;
    AvailableTemplateAccessRoles: KnockoutObservableArray<IRoleSelectionObservable>;
    DefaultUserRoleSettingTypeID: number;
    DefaultTempAccRoleSettingTypeID: number;
    templateSecurityEnabled: KnockoutComputed<boolean>;

    ImageSettingTypes: KnockoutObservableArray<customerSettingsData.ISettingType>;
    DateSettingTypes: string[];
    CustomerTimeSetting: KnockoutObservable<ICustomerSettingObservable>;
    CustomerDateSetting: KnockoutObservable<ICustomerSettingObservable>;
    CustomerTemplateAccessSecuritySetting: KnockoutObservable<ICustomerSettingObservable>;
    MakeTaskReadOnlyWhenCompletedSetting: KnockoutObservable<ICustomerSettingObservable>;

    SettingTypes: customerSettingsData.ISettingType[];
    colorHexValue: KnockoutObservable<string>;
    colorSettingTypeID: KnockoutObservable<number>;
    reportEmailSettingTypeID: KnockoutObservable<number>;
    reportEmailSettingValue: KnockoutObservable<string>;
    imageSettingTypeID: KnockoutObservable<number>;
    settingKey: KnockoutObservable<string>;
    addColorSetting: () => void;
    addReportEmailSetting: () => void;
    removeColorSetting: (index: KnockoutObservable<number>) => void;

    url: string;
    PictureURL: KnockoutObservable<string>;
    imageUploadedNotSavedYet: KnockoutObservable<boolean>;
    TempFileExtension: KnockoutObservable<string>;
    TempFileKey: KnockoutObservable<string>;
    TempFileName: KnockoutObservable<string>;
    openUploadImage: (data: ICustomerImageObservable) => void;
    openCustomerTempImage: (index: KnockoutObservable<number>) => void;
    openUploadImageForCustomer: (data: ICustomerImageObservable) => void;
    openTempImageForCustomer: (index: KnockoutObservable<number>) => void;
    removeImageSetting: (index: KnockoutObservable<number>) => void;
    removeReportEmailSetting: (index: KnockoutObservable<number>) => void;
    showUploadImageModal?: () => void;
    closeUploadImageModal(): void;
    closeViewImageModal(): void;

}

interface IRoleSelectionObservable {
    RoleID: number;
    RoleName: string;
    CustomerSettingID: number;
    IsSelected: KnockoutObservable<boolean>;
}

interface ICustomerSettingObservable {
    ID: KnockoutObservable<number>;
    CustomerSettingTypeID: KnockoutObservable<number>;
    SettingKey: KnockoutObservable<string>;
    Value: KnockoutObservable<string>;
    SettingType: KnockoutObservable<string>;
    IsActive: KnockoutObservable<boolean>;
}

interface ICustomerImageObservable {
    ID: KnockoutObservable<number>;
    CustomerSettingTypeID: KnockoutObservable<number>;
    SettingKey: KnockoutObservable<string>;
    ImageData: KnockoutObservable<string>;
    IsActive: KnockoutObservable<boolean>;
    TempFileKey: KnockoutObservable<string>;
    TempFileName: KnockoutObservable<string>;
    TempFileExtension: KnockoutObservable<string>;
    FileExtension: KnockoutObservable<string>;
}

export class CustomerSettingsController implements IController {
    private viewModel: IViewModel;
    private container: JQuery;
    private fieldsOnLoad: any = {};
    private uploadControl: kendo.ui.Upload;
    constructor() {
        const app: IApplication = Global.app();
        const me: CustomerSettingsController = this;
        this.viewModel = {
            customerID: ko.observable(),
            ColorSettingTypes: ko.observableArray(),
            ReportEmailSettingTypes: ko.observableArray(),
            ImageSettingTypes: ko.observableArray(),
            SettingTypes: [],
            ColorSettings: ko.observableArray(),
            ReportEmailSettings: ko.observableArray(),

            RegistrationCodes: ko.observableArray(),
            registrationCodeValue: ko.observable(),
            registrationCodeSettingTypeID: 0,
            selfRegistrationAllowed: app.global.SelfRegistrationAllowed,

            AvailableUserRoles: ko.observableArray<IRoleSelectionObservable>([]),
            AvailableTemplateAccessRoles: ko.observableArray<IRoleSelectionObservable>([]),
            templateSecurityEnabled: ko.pureComputed(() => me.getSecurityEnabledValue()),
            DefaultTempAccRoleSettingTypeID: 0,
            DefaultUserRoleSettingTypeID: 0,

            CustomerImages: ko.observableArray(),
            CustomerTimeSetting: ko.observable(),
            CustomerDateSetting: ko.observable(),
            CustomerTemplateAccessSecuritySetting: ko.observable(),
            MakeTaskReadOnlyWhenCompletedSetting: ko.observable(),
            DateSettingTypes: [],
            colorHexValue: ko.observable(),
            colorSettingTypeID: ko.observable(),
            reportEmailSettingTypeID: ko.observable(),
            reportEmailSettingValue: ko.observable(),
            imageSettingTypeID: ko.observable(),
            settingKey: ko.observable(),
            imageUploadedNotSavedYet: ko.observable(false),
            TempFileExtension: ko.observable(),
            TempFileKey: ko.observable(),
            TempFileName: ko.observable(),
            url: Global.app().webServiceBaseURL,
            PictureURL: ko.observable(),

            closeUploadImageModal: () => {
                me.container.find("div[data-id='uploadImageDialog']").modal("hide");
            },
            closeViewImageModal: () => {
                me.container.find("div[data-id='viewImageFormDialog']").modal("hide");
            },
            addColorSetting: () => {
                if (me.viewModel.colorSettingTypeID() && me.viewModel.colorHexValue() &&
                    (!me.viewModel.ColorSettings().some((x) => x.CustomerSettingTypeID() === me.viewModel.colorSettingTypeID() &&
                        x.IsActive() === true))) {

                    const settingKey: any = me.viewModel.ColorSettingTypes().filter((x) => x.ID === me.viewModel.colorSettingTypeID());
                    const customerSetting: ICustomerSettingObservable = {
                        ID: ko.observable(0),
                        CustomerSettingTypeID: ko.observable(me.viewModel.colorSettingTypeID()),
                        SettingKey: ko.observable(settingKey[0].SettingKey),
                        Value: ko.observable(me.viewModel.colorHexValue()),
                        SettingType: ko.observable("color"),
                        IsActive: ko.observable(true),
                    };
                    me.viewModel.ColorSettings.push(customerSetting);
                    this.viewModel.colorSettingTypeID(null);
                    me.viewModel.colorHexValue("");
                    me.viewModel.ColorSettingTypes.remove(settingKey[0]);
                }
            },
            openUploadImage: (data: ICustomerImageObservable) => {
                me.viewModel.PictureURL(me.viewModel.url + "/api/CustomerSettings/GetCustomerPicture/" + data.ID());
                me.container.find("div[data-id='viewImageFormDialog']").modal("show");
            },
            openUploadImageForCustomer: (data: ICustomerImageObservable) => {
                me.viewModel.PictureURL(me.viewModel.url +
                    "/api/CustomerSettings/GetCustomerPictureForCustomer/" +
                    this.viewModel.customerID() + "/" + data.ID());

                me.container.find("div[data-id='viewImageFormDialog']").modal("show");
            },
            openTempImageForCustomer: (index: KnockoutObservable<number>) => {
                const customerImageSetting: ICustomerImageObservable = this.viewModel.CustomerImages()[index()];
                if (customerImageSetting.TempFileKey() && customerImageSetting.FileExtension && customerImageSetting.TempFileName()) {
                    me.viewModel.PictureURL(me.viewModel.url +
                        "/api/CustomerSettings/GetTempCustomerPictureForCustomer/" +
                        this.viewModel.customerID() + "/" + customerImageSetting.TempFileName() +
                        "/" + customerImageSetting.TempFileExtension());

                    me.container.find("div[data-id='viewImageFormDialog']").modal("show");
                }
            },
            openCustomerTempImage: (index: KnockoutObservable<number>) => {
                const customerImageSetting: ICustomerImageObservable = this.viewModel.CustomerImages()[index()];

                if (customerImageSetting.TempFileKey() && customerImageSetting.TempFileName() && customerImageSetting.TempFileExtension()) {
                    me.viewModel.PictureURL(me.viewModel.url + "/api/CustomerSettings/GetTempCustomerPicture/"
                        + customerImageSetting.TempFileName() + "/" + customerImageSetting.TempFileExtension());
                    me.container.find("div[data-id='viewImageFormDialog']").modal("show");
                }
            },
            removeImageSetting: (index: KnockoutObservable<number>): void => {
                const selectedImageSetting: ICustomerImageObservable = me.viewModel.CustomerImages()[index()];

                UI.showConfirmDialog("Are you sure you want to delete \"" + selectedImageSetting.SettingKey() + "\"?",
                    () => { me.DeleteCustomerImage(selectedImageSetting); });
            },
            removeColorSetting: (index: KnockoutObservable<number>): any => {
                const selectedSetting: ICustomerSettingObservable = me.viewModel.ColorSettings()[index()];
                UI.showConfirmDialog("Are you sure you want to delete \"" + selectedSetting.SettingKey() + "\"?",
                    () => { me.DeleteColorSetting(selectedSetting); });
            },

            addRegistrationCode: () => {
                if (me.viewModel.registrationCodeValue()) {
                    for (const code of me.viewModel.RegistrationCodes()) {
                        if ((code.Value().toLowerCase() === me.viewModel.registrationCodeValue().toLowerCase()) && (code.IsActive() === true)) {
                            UI.showErrorMessage(["Duplicate Entry. Please add a new value."]);
                            return;
                        }
                    }
                    const customerSetting: ICustomerSettingObservable = {
                        ID: ko.observable(0),
                        CustomerSettingTypeID: ko.observable(me.viewModel.registrationCodeSettingTypeID),
                        SettingKey: ko.observable(Constants.SETTING_KEY_REGISTRATION_CODE),
                        Value: ko.observable(me.viewModel.registrationCodeValue()),
                        SettingType: ko.observable("string"),
                        IsActive: ko.observable(true),
                    };
                    me.viewModel.RegistrationCodes.push(customerSetting);
                    me.viewModel.registrationCodeValue("");
                }
            },

            removeRegistrationCode: (index: KnockoutObservable<number>): any => {
                const selectedSetting: ICustomerSettingObservable = me.viewModel.RegistrationCodes()[index()];
                UI.showConfirmDialog("Are you sure you want to delete \"" + selectedSetting.SettingKey() + "\"?",
                    () => { me.DeleteRegistrationCode(selectedSetting); });
            },

            addReportEmailSetting: () => {
                if (me.viewModel.reportEmailSettingTypeID() && me.viewModel.reportEmailSettingValue()) {
                    for (const email of me.viewModel.ReportEmailSettings()) {
                        if ((email.Value().toLowerCase() === me.viewModel.reportEmailSettingValue().toLowerCase()) && (email.IsActive() === true)) {
                            UI.showErrorMessage(["This email is already added to the list. Please add a new email"]);
                            return;
                        }
                    }

                    const settingKey: any = me.viewModel.ReportEmailSettingTypes().filter((x) => x.ID === me.viewModel.reportEmailSettingTypeID());
                    const customerSetting: ICustomerSettingObservable = {
                        ID: ko.observable(0),
                        CustomerSettingTypeID: ko.observable(me.viewModel.reportEmailSettingTypeID()),
                        SettingKey: ko.observable(settingKey[0].SettingKey),
                        Value: ko.observable(me.viewModel.reportEmailSettingValue()),
                        SettingType: ko.observable("string"),
                        IsActive: ko.observable(true),
                    };
                    me.viewModel.ReportEmailSettings.push(customerSetting);
                    this.viewModel.reportEmailSettingTypeID(null);
                    me.viewModel.reportEmailSettingValue("");
                }
            },
            removeReportEmailSetting: (index: KnockoutObservable<number>): any => {
                const selectedSetting: ICustomerSettingObservable = me.viewModel.ReportEmailSettings()[index()];
                UI.showConfirmDialog("Are you sure you want to delete \"" + selectedSetting.SettingKey() + "\"?",
                    () => { me.DeleteReportEmailSetting(selectedSetting); });
            },
        };

        this.viewModel.DateSettingTypes = Constants.DATE_FORMATS;
    }

    public haveFieldsChanged(): boolean {
        const fieldValues: string = ko.toJSON(this.getAllCustomerSettings());
        return fieldValues !== this.fieldsOnLoad;
    }

    public appRouterID(params: string[]): string {
        return "CustomerSettings";
    }

    public title(): string {
        return "Customer Settings";
    }

    public subTitle(): string {
        return "";
    }

    public render(container: JQuery<HTMLElement>): JQueryPromise<any> {
        const deferred: JQuery.Deferred<any, any, any> = $.Deferred();

        this.container = container;

        // initialize kendo upload control for uploadimage
        this.uploadControl = this.container.find("input[data-id='uploadImageControl']").kendoUpload({
            async: {
                saveUrl: "save",
                removeUrl: "remove",
                autoUpload: true,
            },
            validation: {
                allowedExtensions: [".jpg", ".jpeg", ".png"],  // TODO - read extensions from DocumentFileTypeAllowed table
            },
            upload: this.onImageUpload.bind(this),
            success: this.onUploadSuccess.bind(this),
            multiple: false,
        }).data("kendoUpload");

        ko.applyBindings(this.viewModel, this.container[0]);

        deferred.resolve();

        return deferred.promise();
    }

    public load(params: any[]): JQueryPromise<any> {
        if (!params || params.length === 0) {
            return this.loadSettings();
        } else {
            this.viewModel.customerID(params[0]);
            return this.loadSettingsForCustomer(this.viewModel.customerID());
        }
    }

    public save(): JQueryPromise<any> {
        const me: CustomerSettingsController = this;
        const app: IApplication = Global.app();
        let waitContainer: JQuery;
        const deferred: JQuery.Deferred<any, any, any> = $.Deferred();

        waitContainer = UI.showWaitIndicator(me.container);

        const saveMethod = me.viewModel.customerID() ?
            () => customerSettingsData.SaveForCustomer(this.viewModel.customerID(), this.getAllCustomerSettings()) :
            () => customerSettingsData.SaveCustomerSettings(this.getAllCustomerSettings());

        const loadMethod = me.viewModel.customerID() ?
            () => this.loadSettingsForCustomer(this.viewModel.customerID()) :
            () => this.loadSettings();

        saveMethod()
            .done((response: Data.IResponse<customerSettingsData.ICustomerAllSettingsSaveResponse>) => {
                if (response.Success) {
                    app.pageManager().refreshCurrentPageTitle();
                    this.viewModel.ColorSettings.removeAll();
                    this.viewModel.CustomerImages.removeAll();
                    loadMethod();
                    UI.showNotificationMessage(Constants.PHRASE_RECORDSAVED, UI.messageTypeValues.success);
                    deferred.resolve();
                } else {
                    UI.showErrorMessage(response.ErrorMessages);
                    deferred.reject();
                }
            })
            .fail(() => {
                deferred.reject();
            })
            .always(() => {
                UI.hideWaitIndicator(me.container, waitContainer);
            });

        return deferred.promise();
    }

    private DeleteColorSetting(selectedCustomerSetting: ICustomerSettingObservable): void {
        const me: CustomerSettingsController = this;
        const settingKey: any = me.viewModel.SettingTypes.filter((x) => x.ID === selectedCustomerSetting.CustomerSettingTypeID());

        if (selectedCustomerSetting.ID() === 0) {
            me.viewModel.ColorSettings.remove(selectedCustomerSetting);
        } else {
            selectedCustomerSetting.IsActive(false);
        }
        me.viewModel.ColorSettingTypes.push(settingKey[0]);
    }

    private DeleteCustomerImage(selectedCustomerImageSetting: ICustomerImageObservable): void {
        const me: CustomerSettingsController = this;
        const settingKey: any = me.viewModel.SettingTypes.filter((x) => x.ID === selectedCustomerImageSetting.CustomerSettingTypeID());

        if (selectedCustomerImageSetting.ID() === 0) {
            me.viewModel.CustomerImages.remove(selectedCustomerImageSetting);
        } else {
            selectedCustomerImageSetting.IsActive(false);
        }
        me.viewModel.ImageSettingTypes.push(settingKey[0]);
    }

    private DeleteReportEmailSetting(selectedCustomerSetting: ICustomerSettingObservable) {
        const me: CustomerSettingsController = this;

        if (selectedCustomerSetting.ID() === 0) {
            me.viewModel.ReportEmailSettings.remove(selectedCustomerSetting);
        } else {
            selectedCustomerSetting.IsActive(false);
        }
    }
    private DeleteRegistrationCode(selectedCustomerSetting: ICustomerSettingObservable) {
        const me: CustomerSettingsController = this;

        if (selectedCustomerSetting.ID() === 0) {
            me.viewModel.RegistrationCodes.remove(selectedCustomerSetting);
        } else {
            selectedCustomerSetting.IsActive(false);
        }
    }

    private getAllCustomerSettings(): customerSettingsData.ICustomerAllSettings {
        const customerAllSettings: customerSettingsData.ICustomerAllSettings = {
            CustomerSettings: this.getCustomerSettings(),
            CustomerImages: this.getCustomerImages(),
        };
        return customerAllSettings;
    }

    private getCustomerSetting(customerSetting: ICustomerSettingObservable): customerSettingsData.ICustomerSetting {
        const setting: customerSettingsData.ICustomerSetting = {
            ID: customerSetting.ID(),
            CustomerSettingTypeID: customerSetting.CustomerSettingTypeID(),
            SettingKey: customerSetting.SettingKey(),
            SettingType: customerSetting.SettingType(),
            IsActive: customerSetting.IsActive(),
            Value: customerSetting.Value(),

        };
        return setting;
    }

    private getCustomerSettings(): customerSettingsData.ICustomerSetting[] {
        const customerSettings: customerSettingsData.ICustomerSetting[] = [];
        const me: CustomerSettingsController = this;
        this.viewModel.ColorSettings().forEach((customerSetting: ICustomerSettingObservable) => {
            customerSettings.push(me.getCustomerSetting(customerSetting));
        });

        this.viewModel.ReportEmailSettings().forEach((customerSetting: ICustomerSettingObservable) => {
            customerSettings.push(me.getCustomerSetting(customerSetting));
        });

        this.viewModel.RegistrationCodes().forEach((customerSetting: ICustomerSettingObservable) => {
            customerSettings.push(me.getSettingFromObservable(customerSetting));
        });

        this.viewModel.AvailableUserRoles().forEach((role: IRoleSelectionObservable) => {
            if (role.IsSelected() || role.CustomerSettingID > 0) {
                customerSettings.push(
                    me.geCustomerSettingFromObservable(role, Constants.SETTING_KEY_DEFAULT_USER_ROLE, this.viewModel.DefaultUserRoleSettingTypeID));
            }
        });

        this.viewModel.AvailableTemplateAccessRoles().forEach((role: IRoleSelectionObservable) => {
            if (role.IsSelected() || role.CustomerSettingID > 0) {
                customerSettings.push(
                    me.geCustomerSettingFromObservable(role, Constants.SETTING_KEY_DEFAULT_TEMPLATEACCESS_ROLE, this.viewModel.DefaultTempAccRoleSettingTypeID));
            }
        });

        if (me.viewModel.CustomerTimeSetting().Value()) {
            customerSettings.push(me.getSettingFromObservable(me.viewModel.CustomerTimeSetting()));
        }

        if (me.viewModel.CustomerDateSetting().Value()) {
            customerSettings.push(me.getSettingFromObservable(me.viewModel.CustomerDateSetting()));
        }

        if (me.viewModel.CustomerTemplateAccessSecuritySetting().Value()) {
            customerSettings.push(me.getSettingFromObservable(me.viewModel.CustomerTemplateAccessSecuritySetting()));
        }

        if (me.viewModel.MakeTaskReadOnlyWhenCompletedSetting().Value()) {
            customerSettings.push(me.getSettingFromObservable(me.viewModel.MakeTaskReadOnlyWhenCompletedSetting()));
        }

        return customerSettings;
    }

    private geCustomerSettingFromObservable(role: IRoleSelectionObservable, settingKey: string, customerSettingTypeID: number) {
        const setting: customerSettingsData.ICustomerSetting = {
            ID: role.CustomerSettingID,
            CustomerSettingTypeID: customerSettingTypeID,
            SettingKey: settingKey,
            SettingType: "string",
            IsActive: role.IsSelected(),
            Value: role.RoleID.toString(),
        };
        return setting;
    }

    private getSettingFromObservable(settingObservable: ICustomerSettingObservable): customerSettingsData.ICustomerSetting {
        const setting: customerSettingsData.ICustomerSetting = {
            ID: settingObservable.ID(),
            CustomerSettingTypeID: settingObservable.CustomerSettingTypeID(),
            SettingKey: settingObservable.SettingKey(),
            SettingType: settingObservable.SettingType(),
            IsActive: settingObservable.IsActive(),
            Value: settingObservable.Value(),

        };
        return setting;
    }

    private getCustomerImages(): customerSettingsData.ICustomerImage[] {
        const customerImages: customerSettingsData.ICustomerImage[] = [];
        this.viewModel.CustomerImages().forEach((customerImage: ICustomerImageObservable) => {
            const imageSetting: customerSettingsData.ICustomerImage = {
                ID: customerImage.ID(),
                CustomerSettingTypeID: customerImage.CustomerSettingTypeID(),
                SettingKey: customerImage.SettingKey(),
                ImageData: customerImage.ImageData(),
                IsActive: customerImage.IsActive(),
                TempFileKey: customerImage.TempFileKey(),
                FileExtension: customerImage.FileExtension(),
            };
            customerImages.push(imageSetting);
        });
        return customerImages;
    }

    private onImageUpload(e: kendo.ui.UploadUploadEvent): void {
        const url: string = Global.app().webServiceBaseURL;

        if (this.uploadControl.options.async) {
            if (this.viewModel.customerID()) {
                this.uploadControl.options.async.saveUrl = url + "/api/CustomerSettings/UploadCustomerImageForCustomer/" + this.viewModel.customerID();
            } else {
                this.uploadControl.options.async.saveUrl = url + "/api/CustomerSettings/UploadCustomerImage";
            }
        }
    }

    private onUploadSuccess(e: kendo.ui.UploadSuccessEvent): void {
        const me: CustomerSettingsController = this;
        me.viewModel.TempFileKey(e.response.TempFileKey);
        me.viewModel.TempFileName(e.response.TempFileName);
        me.viewModel.TempFileExtension(e.response.TempFileExtension);

        if (me.viewModel.TempFileKey()) {
            const fileExtension: string = "." + me.viewModel.TempFileExtension().toLowerCase();
            const settingKey: any = me.viewModel.ImageSettingTypes().filter((x) => x.ID === me.viewModel.imageSettingTypeID());
            const newCustomerImageSetting: ICustomerImageObservable = {
                ID: ko.observable(0),
                CustomerSettingTypeID: ko.observable(me.viewModel.imageSettingTypeID()),
                SettingKey: ko.observable(settingKey[0].SettingKey),
                TempFileKey: ko.observable(me.viewModel.TempFileKey()),
                TempFileName: ko.observable(me.viewModel.TempFileName()),
                TempFileExtension: ko.observable(me.viewModel.TempFileExtension().toLowerCase()),
                ImageData: ko.observable(""),
                IsActive: ko.observable(true),
                FileExtension: ko.observable(fileExtension),
            };
            me.viewModel.CustomerImages.push(newCustomerImageSetting);
            me.viewModel.ImageSettingTypes.remove(settingKey[0]);
            me.viewModel.imageSettingTypeID(null);

        }
    }

    private loadSettings(): JQueryPromise<any> {
        const me: CustomerSettingsController = this;
        const deferred: JQuery.Deferred<any, any, any> = $.Deferred();
        const waitContainer: JQuery<HTMLElement> = UI.showWaitIndicator(this.container);

        customerSettingsData.GetAllSettings()
            .done((response: Data.IResponse<customerSettingsData.ICustomerMasterSettings>) => {
                if (response.Success) {
                    me.updateViewModel(response.Payload);
                } else {
                    UI.showErrorMessage(response.ErrorMessages);
                }
            })
            .always(() => {
                UI.hideWaitIndicator(me.container, waitContainer);
                deferred.resolve();
            });

        return deferred.promise();
    }

    private loadSettingsForCustomer(customerID: number): JQueryPromise<any> {
        const me: CustomerSettingsController = this;
        const deferred: JQuery.Deferred<any, any, any> = $.Deferred();
        const waitContainer: JQuery<HTMLElement> = UI.showWaitIndicator(this.container);

        customerSettingsData.GetAllSettingsForCustomer(customerID)
            .done((response: Data.IResponse<customerSettingsData.ICustomerMasterSettings>) => {
                if (response.Success) {
                    me.updateViewModel(response.Payload);
                } else {
                    UI.showErrorMessage(response.ErrorMessages);
                }
            })
            .always(() => {
                UI.hideWaitIndicator(me.container, waitContainer);
                deferred.resolve();
            });
        return deferred.promise();
    }

    private updateViewModel(master: customerSettingsData.ICustomerMasterSettings): void {
        const me: CustomerSettingsController = this;
        me.viewModel.ReportEmailSettings.removeAll();
        me.viewModel.RegistrationCodes.removeAll();
        me.viewModel.ColorSettings.removeAll();
        me.viewModel.SettingTypes = master.SettingTypes;

        me.viewModel.AvailableUserRoles(me.getRoleSelectionObservablesForUserRoles(master));
        me.viewModel.AvailableTemplateAccessRoles(me.getRoleSelectionObservablesForTemplateAccessRoles(master));
        master.CustomerSettings.forEach((customerSetting: customerSettingsData.ICustomerSetting) => {
            if (customerSetting.SettingType === Constants.SETTING_TYPE_COLOR) {
                me.viewModel.ColorSettings.push(me.CustomerSetting(customerSetting));
            }

            if (customerSetting.SettingKey === Constants.SETTING_KEY_REPORT_RECIPIENT_EMAIL_ADDRESS ||
                customerSetting.SettingKey === Constants.SETTING_KEY_REPORT_RECIPIENT_EMAIL_FIELD_PATH) {
                me.viewModel.ReportEmailSettings.push(me.CustomerSetting(customerSetting));
            }

            if (customerSetting.SettingKey === Constants.SETTING_KEY_REGISTRATION_CODE) {
               me.viewModel.RegistrationCodes.push(me.CustomerSetting(customerSetting));
            }
        });

        master.CustomerImages.forEach((customerImage: customerSettingsData.ICustomerImage) => {
            me.viewModel.CustomerImages.push(me.CustomerImage(customerImage));
        });

        me.viewModel.ReportEmailSettingTypes.removeAll();
        master.SettingTypes.forEach((settingType) => {
            if (settingType.SettingKey === Constants.SETTING_KEY_REPORT_RECIPIENT_EMAIL_ADDRESS ||
                settingType.SettingKey === Constants.SETTING_KEY_REPORT_RECIPIENT_EMAIL_FIELD_PATH) {
                me.viewModel.ReportEmailSettingTypes.push(settingType);
            }
        });

        me.viewModel.registrationCodeSettingTypeID = master.SettingTypes.filter((x) => x.SettingKey === Constants.SETTING_KEY_REGISTRATION_CODE)[0].ID;
        me.viewModel.DefaultUserRoleSettingTypeID = master.SettingTypes.filter((x) => x.SettingKey === Constants.SETTING_KEY_DEFAULT_USER_ROLE)[0].ID;
        me.viewModel.DefaultTempAccRoleSettingTypeID = master.SettingTypes.filter((x) => x.SettingKey === Constants.SETTING_KEY_DEFAULT_TEMPLATEACCESS_ROLE)[0].ID;

        me.viewModel.CustomerDateSetting(me.createCustomerSettingObservable(master, Constants.DEFAULT_DATE_FORMAT, Constants.SETTING_KEY_DATEFORMAT));
        me.viewModel.CustomerTimeSetting(me.createCustomerSettingObservable(master, Constants.DEFAULT_TIME_FORMAT, Constants.SETTING_KEY_TIMEFORMAT));
        me.viewModel.CustomerTemplateAccessSecuritySetting(me.createCustomerSettingObservable(master, "false", Constants.SETTING_KEY_TEMPLATE_ACCESS_SECURITY));
        me.viewModel.MakeTaskReadOnlyWhenCompletedSetting(me.createCustomerSettingObservable(master, "false", Constants.SETTING_KEY_MAKE_TASK_READ_ONLY_WHEN_COMPLETED));

        if (me.viewModel.ColorSettings().length === 0) {
            // if the customerSettings list is empty add all color settings to the dropdown
            me.viewModel.ColorSettingTypes(me.viewModel.SettingTypes.filter((x) => x.SettingType === Constants.SETTING_TYPE_COLOR));
        } else {
            // fill the colorSettings dropdown, that are not added to the customerSettings list
            let colorSettings: customerSettingsData.ISettingType[] = me.viewModel.SettingTypes.filter((x) => x.SettingType === Constants.SETTING_TYPE_COLOR);
            master.CustomerSettings.forEach((customerSetting: customerSettingsData.ICustomerSetting) => {
                colorSettings = colorSettings.filter((x) => x.ID !== customerSetting.CustomerSettingTypeID);
            });
            me.viewModel.ColorSettingTypes(colorSettings);
        }

        if (master.CustomerImages.length === 0) {
            // if the customerimage list is empty, add all the image settings to the imagesettings dropdown
            me.viewModel.ImageSettingTypes(me.viewModel.SettingTypes.filter((x) => x.SettingType === Constants.SETTING_TYPE_IMAGE));
        } else {
            let imageSettings: customerSettingsData.ISettingType[] = me.viewModel.SettingTypes.filter((x) => x.SettingType === Constants.SETTING_TYPE_IMAGE);
            // fill the image setting dropdown with the records that doesn't have images in customerImages
            master.CustomerImages.forEach((customerImage: customerSettingsData.ICustomerImage) => {
                imageSettings = imageSettings.filter((x) => x.ID !== customerImage.CustomerSettingTypeID);
            });
            me.viewModel.ImageSettingTypes(imageSettings);
        }

        me.viewModel.showUploadImageModal = () => {
            me.container.find("div[data-id='uploadImageDialog']").modal("show");
            me.container.find(".k-upload-status-total").remove();
        };
        this.fieldsOnLoad = ko.toJSON(this.getAllCustomerSettings());
    }

    private getRoleSelectionObservablesForUserRoles(master: customerSettingsData.ICustomerMasterSettings): IRoleSelectionObservable[] {
        const roleSelectionObservables: IRoleSelectionObservable[] = [];
        master.UserRoles.forEach((availableRole: AvailableRoles.IUserRoles) => {
             const customerSettings = master.CustomerSettings.filter((x) => x.Value === availableRole.ID.toString()
                                                                             && x.SettingKey === Constants.SETTING_KEY_DEFAULT_USER_ROLE);
             const templateAccessRole: IRoleSelectionObservable = {
                 RoleID: availableRole.ID,
                 RoleName: availableRole.RoleName,
                 IsSelected: ko.observable(customerSettings.length > 0),
                 CustomerSettingID: customerSettings.length > 0 ? customerSettings[0].ID : 0,
             };
             roleSelectionObservables.push(templateAccessRole);
        });
        return roleSelectionObservables;
    }

    private getRoleSelectionObservablesForTemplateAccessRoles(master: customerSettingsData.ICustomerMasterSettings): IRoleSelectionObservable[] {
        const roleSelectionObservables: IRoleSelectionObservable[] = [];
        master.TemplateAccessRoles.forEach((availableRole: AvailableRoles.ITemplateAccessRoles) => {
             const customerSettings = master.CustomerSettings.filter((x) => x.Value === availableRole.ID.toString()
                                                                             && x.SettingKey === Constants.SETTING_KEY_DEFAULT_TEMPLATEACCESS_ROLE);
             const templateAccessRole: IRoleSelectionObservable = {
                 RoleID: availableRole.ID,
                 RoleName: availableRole.Name,
                 IsSelected: ko.observable(customerSettings.length > 0),
                 CustomerSettingID: customerSettings.length > 0 ? customerSettings[0].ID : 0,
             };
             roleSelectionObservables.push(templateAccessRole);
        });
        return roleSelectionObservables;
    }

    private createCustomerSettingObservable(master: customerSettingsData.ICustomerMasterSettings,
        defaultValue: string, settingKey: string): ICustomerSettingObservable {

        const settingIndex: number = master.CustomerSettings.map((x) => x.SettingKey).indexOf(settingKey);

        const settingType: customerSettingsData.ISettingType[] = master.SettingTypes.filter((x) => x.SettingKey === settingKey);

        const settingObservable: ICustomerSettingObservable = {
            ID: ko.observable(master.CustomerSettings[settingIndex] ? master.CustomerSettings[settingIndex].ID : 0),
            CustomerSettingTypeID: ko.observable(master.CustomerSettings[settingIndex] ?
                master.CustomerSettings[settingIndex].CustomerSettingTypeID : settingType[0].ID),
            SettingKey: ko.observable(master.CustomerSettings[settingIndex] ? master.CustomerSettings[settingIndex].SettingKey : settingType[0].SettingKey),
            Value: ko.observable(master.CustomerSettings[settingIndex] ? master.CustomerSettings[settingIndex].Value : defaultValue),
            SettingType: ko.observable(master.CustomerSettings[settingIndex] ? master.CustomerSettings[settingIndex].SettingType : settingType[0].SettingType),
            IsActive: ko.observable(master.CustomerSettings[settingIndex] ? master.CustomerSettings[settingIndex].IsActive : true),
        };

        return settingObservable;
    }

    private CustomerImage(customerImage: customerSettingsData.ICustomerImage): ICustomerImageObservable {
        const vmCustomerImage: ICustomerImageObservable = {
            ID: ko.observable(customerImage.ID),
            CustomerSettingTypeID: ko.observable(customerImage.CustomerSettingTypeID),
            SettingKey: ko.observable(customerImage.SettingKey),
            FileExtension: ko.observable(customerImage.FileExtension),
            TempFileKey: ko.observable(customerImage ? customerImage.TempFileKey : ""),
            ImageData: ko.observable(customerImage ? customerImage.ImageData : ""),
            IsActive: ko.observable(customerImage.IsActive),
            TempFileExtension: ko.observable(""),
            TempFileName: ko.observable(""),
        };
        return vmCustomerImage;
    }

    private CustomerSetting(customerSetting: customerSettingsData.ICustomerSetting): ICustomerSettingObservable {
        const vmCustomerSetting: ICustomerSettingObservable = {
            ID: ko.observable(customerSetting.ID),
            CustomerSettingTypeID: ko.observable(customerSetting.CustomerSettingTypeID),
            SettingKey: ko.observable(customerSetting.SettingKey),
            Value: ko.observable(customerSetting.Value),
            SettingType: ko.observable(customerSetting.SettingType),
            IsActive: ko.observable(customerSetting.IsActive),
        };
        return vmCustomerSetting;
    }
    private getSecurityEnabledValue(): boolean {
        const me: CustomerSettingsController = this;
        let enabled: boolean = true;
        if (me.viewModel.CustomerTemplateAccessSecuritySetting() === undefined ||
            me.viewModel.CustomerTemplateAccessSecuritySetting().Value() === "false") {
           enabled = false;
        }
        return enabled;
    }
}



// WEBPACK FOOTER //
// ./node_modules/tslint-loader??ref--7!./src/script/controllers/customerSettings.ts